<template>
	<section
		class="scene-places-index"
		v-bind:class="{
			'state-show-search': showSearch,
		}"
	>
		<ChalkyUtilityTabGroup v-bind:tabs="availableTabs" v-bind:useUrl="true" v-on:tab-change="Handle_OnTabChange">
			<template #after>
				<div class="actions" v-if="currentActiveTab === 'nearby'">
					<button class="control round size-x-small type-search" v-on:click="Handle_OnClickSearchToggle">
						Search
					</button>
				</div>
			</template>

			<template v-slot="{ activeTab }">
				<div v-if="activeTab === 'nearby'">
					<ViewUiSearchBar
						v-bind:visible="showSearch"
						v-on:clear="Handle_OnClearSearch"
						v-on:submit="Handle_OnSubmitSearch"
					/>

					<ViewVenueListPreview
						emptyState="loading"
						v-bind:venueCollection="nearbyVenueCollection"
						v-on:venue:select="Handle_OnSelectVenue"
					/>
				</div>

				<div v-if="activeTab === 'favorites'">
					<ViewVenueListPreview
						v-bind:emptyState="favoriteVenueCollection.uniqueKey ? 'no-favorites' : 'loading'"
						v-bind:venueCollection="favoriteVenueCollection"
						v-on:venue:select="Handle_OnSelectVenue"
					/>
				</div>

				<div v-show="activeTab === 'map'">
					<section>
						<ViewUiMediaMap
							v-bind:mapLatitude="$store.getters['location/latitude']"
							v-bind:mapLongitude="$store.getters['location/longitude']"
							v-bind:mapZoom="13"
							v-on:marker:click="Handle_OnSelectVenue"
						/>

						<section class="map-controls">
							<RouterLink class="btn size-small" v-bind:to="{ name: 'PageMap' }">Expand</RouterLink>
						</section>
					</section>
				</div>

				<div v-show="activeTab === 'submit'">
					<ViewVenueSubmissionForm />
				</div>
			</template>
		</ChalkyUtilityTabGroup>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import SceneBase from '@/Core/Scene.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * Places index scene with tabbed navigation for nearby, favorites,
	 * map, and submission views. Handles search, geocoding, distance
	 * sorting, and infinite scroll.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Scene/Places
	 * @project ChalkySticks App
	 */
	@Component
	export default class ScenePlacesIndex extends SceneBase {
		/**
		 * Available tabs based on authentication state.
		 *
		 * @return string[]
		 */
		protected get availableTabs(): string[] {
			if (this.$store.getters['authentication/authenticated']) {
				return ['Nearby', 'Favorites', 'Map']; // , 'Submit'];
			}

			return ['Nearby', 'Map']; // , 'Submit'];
		}

		/**
		 * Collection of user's favorite venues
		 *
		 * @type ChalkySticks.Collection.Venue
		 */
		@Prop()
		public favoriteVenueCollection!: ChalkySticks.Collection.Venue;

		/**
		 * Collection of nearby venues
		 *
		 * @type ChalkySticks.Collection.Venue
		 */
		@Prop()
		public nearbyVenueCollection!: ChalkySticks.Collection.Venue;

		/**
		 * Current active tab slug.
		 *
		 * @type string
		 */
		protected currentActiveTab: string = '';

		/**
		 * Latitude from search geocoding.
		 *
		 * @type number
		 */
		protected searchLatitude: number = 0;

		/**
		 * Longitude from search geocoding.
		 *
		 * @type number
		 */
		protected searchLongitude: number = 0;

		/**
		 * Whether the search form is visible.
		 *
		 * @type boolean
		 */
		protected showSearch: boolean = false;

		/**
		 * Initialize scene and fetch data based on current tab.
		 *
		 * @return void
		 */
		@mounted
		public setup(): void {
			this.currentActiveTab = this.$route.query.tab?.toString() || 'nearby';

			// If we have a search query in the URL, open search and execute it
			const searchParam = this.$route.query.q?.toString() || '';

			if (searchParam) {
				this.showSearch = true;
				this.executeSearch(searchParam);
				return;
			}

			switch (this.$route.query.tab) {
				case 'favorites':
					this.fetchFavorites();
					break;

				case 'map':
					// Map is handled by the ViewMap component
					break;

				case 'nearby':
				default:
					this.fetchNearby();
					break;
			}
		}

		/**
		 * Attach event listeners on mount.
		 *
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			this.nearbyVenueCollection.on('complete', this.Handle_OnNearbyVenueCollectionComplete);
		}

		/**
		 * Detach event listeners before destroy.
		 *
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			this.nearbyVenueCollection.off('complete', this.Handle_OnNearbyVenueCollectionComplete);
		}

		/**
		 * Set location coordinates on the nearby collection.
		 *
		 * @return void
		 */
		@mounted
		protected setLocationCoordinates(): void {
			const { latitude, longitude } = this.$store.getters['location/roughCoordinates'];

			this.nearbyVenueCollection.setQueryParams({
				latitude: latitude,
				longitude: longitude,
			});
		}

		/**
		 * Fetch nearby venues based on current location.
		 *
		 * @return void
		 */
		protected fetchNearby(): void {
			const { latitude, longitude } = this.$store.getters['location/roughCoordinates'];

			// Check if last fetched was within a minute
			if (Date.now() - this.nearbyVenueCollection.timeCompleted < 1000 * 60) {
				return;
			}

			// Get latest coordinates
			this.nearbyVenueCollection.setQueryParams({
				latitude: latitude,
				longitude: longitude,
			});

			// Fetch
			this.nearbyVenueCollection.fetch();
		}

		/**
		 * Fetch user's favorite venues.
		 *
		 * @return void
		 */
		protected fetchFavorites(): void {
			const userModel = this.$store.getters['authentication/user'];

			// Check if last fetched was within a minute
			if (Date.now() - this.favoriteVenueCollection.timeCompleted < 1000 * 60) {
				return;
			}

			this.favoriteVenueCollection.setQueryParam('reaction_from', userModel?.id);
			this.favoriteVenueCollection.fetch();
		}

		/**
		 * Gets the currently active collection based on selected tab.
		 *
		 * @return ChalkySticks.Collection.Venue
		 */
		protected getActiveCollection(): ChalkySticks.Collection.Venue {
			return this.currentActiveTab === 'favorites' ? this.favoriteVenueCollection : this.nearbyVenueCollection;
		}

		/**
		 * Check if currently performing a search.
		 *
		 * @return boolean
		 */
		protected isSearching(): boolean {
			return this.showSearch && this.searchLatitude !== 0 && this.searchLongitude !== 0;
		}

		/**
		 * Sort venue models by distance from current location or search location.
		 *
		 * @return void
		 */
		protected sortModelsByDistance(): void {
			this.nearbyVenueCollection.models = this.nearbyVenueCollection.models.sort((venueA, venueB): number => {
				let { latitude, longitude } = this.$store.getters['location/coordinates'];

				// If we're searching, use the searched coordinates
				if (this.isSearching()) {
					latitude = this.searchLatitude;
					longitude = this.searchLongitude;
				}

				const distanceToA = venueA.getDistance(latitude, longitude);
				const distanceToB = venueB.getDistance(latitude, longitude);

				return distanceToA - distanceToB;
			});
		}

		/**
		 * Fetches next page of results for the active collection.
		 * Called by the parent Page via $refs when AutoScroll triggers.
		 *
		 * @return Promise<void>
		 */
		public async loadMore(): Promise<void> {
			if (['nearby', 'favorites'].includes(this.currentActiveTab) === false) {
				return;
			}

			try {
				const activeCollection = this.getActiveCollection();

				// 5 is an arbitrary amount to say that we have enough
				// results where we're probably scrolling
				if (activeCollection.length > 5) {
					await activeCollection.nextPage(true);
				}
			} catch (error) {
				console.error('Error fetching more results:', error);
			}
		}

		/**
		 * Execute a search by geocoding the address and fetching results.
		 *
		 * @param string address
		 * @return Promise<void>
		 */
		protected async executeSearch(address: string): Promise<void> {
			const query = address.trim().toLowerCase();

			// Exit if we don't have an address
			if (query.length === 0) {
				return;
			}

			// Fetch geocode results
			const geocodeModel = await ChalkySticks.Model.Geocode.search(query);
			const { latitude, longitude } = geocodeModel.getLocation();

			// Save search coordinates for sorting
			this.searchLatitude = latitude;
			this.searchLongitude = longitude;

			// Track event
			ChalkySticks.Utility.Analytics.track('places:search', {
				address: query,
				latitude: latitude,
				longitude: longitude,
			});

			this.nearbyVenueCollection.setQueryParam('latitude', latitude.toString());
			this.nearbyVenueCollection.setQueryParam('longitude', longitude.toString());
			this.nearbyVenueCollection.setQueryParam('page', '1');

			// Query new results
			await this.nearbyVenueCollection.fetch();
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Handle clearing the search. Resets coordinates and fetches nearby.
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnClearSearch(): Promise<void> {
			this.searchLatitude = 0;
			this.searchLongitude = 0;

			// Clear
			this.nearbyVenueCollection.clear();
			this.nearbyVenueCollection.setQueryParam('page', '1');

			// Fetch nearby
			this.fetchNearby();
		}

		/**
		 * Handle toggling search form visibility.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickSearchToggle(e: PointerEvent): Promise<void> {
			this.showSearch = !this.showSearch;

			ChalkySticks.Utility.Analytics.track('places:search.open');
		}

		/**
		 * Handle nearby venue collection fetch complete.
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnNearbyVenueCollectionComplete(): Promise<void> {
			this.sortModelsByDistance();
		}

		/**
		 * Handle venue selection from list or map. Emits to parent for navigation.
		 *
		 * @param ChalkySticks.Model.Venue venueModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectVenue(venueModel: ChalkySticks.Model.Venue): Promise<void> {
			this.$emit('venue:select', venueModel);
		}

		/**
		 * Handle search submission from SearchBar. The query param is already
		 * in the URL at this point; we just need to execute the search.
		 *
		 * @param string query
		 * @return Promise<void>
		 */
		protected async Handle_OnSubmitSearch(query: string): Promise<void> {
			await this.executeSearch(query);
		}

		/**
		 * Handle tab change events.
		 *
		 * @param string slug
		 * @return Promise<void>
		 */
		protected async Handle_OnTabChange(slug: string): Promise<void> {
			// Update current active tab
			this.currentActiveTab = slug;

			// Reset pages on both collections
			this.nearbyVenueCollection.setQueryParam('page', '1');
			this.favoriteVenueCollection.setQueryParam('page', '1');

			// Tab change
			ChalkySticks.Utility.Analytics.track('places:tab.change', {
				tab: slug,
			});

			switch (slug) {
				case 'nearby':
					await this.fetchNearby();
					break;

				case 'favorites':
					await this.fetchFavorites();
					break;

				case 'map':
				case 'submit':
					// Do nothing
					break;

				default:
					console.warn('Unknown tab slug:', slug);
			}
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.scene-places-index {
		display: flex;
		flex-direction: column;
		flex-grow: 1;

		.view-map {
			border-radius: var(--border-radius);
			overflow: hidden;

			.chalky.venue-map .gm-bundled-control {
				display: none;
			}
		}

		.map-controls {
			bottom: 1.5rem;
			position: absolute;
			right: 1.5rem;
		}
	}

	// State
	// ---------------------------------------------------------------------------

	.scene-places-index:not(.state-show-search) {
		.view-ui-search-bar {
			display: none;
		}

		button.type-search {
			opacity: 0.33;
		}
	}
</style>
